import React, { useState, useEffect } from 'react';
import { assetType } from 'helpers/constants';
import { translate } from 'helpers/translateHelper';
import { formatDateTime, normalizeUtcTimestamp } from 'helpers/dateTimeFormatter';
import { map } from 'lodash';
import { bearingFrequencyTypes, SPEED_UNIT } from '../FFT2D/constants';
import { echartColors, echartHarmonicsLabel, echartViews } from './constants';
import { renderToStaticMarkup } from 'react-dom/server';
import FrequencyView from './Tooltip/FrequencyView';
import SpeedView from './Tooltip/SpeedView';
import { getSpecialFrequenciesMagnitudes, getSpectrumDataForFrequency, getMaxValue, getMinValue } from './helpers';
import { MAX_PRECISION } from '../constants';
import { setMaxPrecision } from 'helpers/formatters';

const chartConfig = {
    backgroundColor: 'rgba(249, 249, 250, 1)',
    animation: false,
    enableNoDataZoom: true,
    lazyUpdate: true,
    xAxis3D: {
        nameTextStyle: {
            fontSize: 14,
        },
        nameGap: 30,
        nameLocation: 'end',
        axisLabel: {
            fontSize: 10,
            margin: 12,
            formatter: function (value) {
                return parseFloat(value).toFixed(2);
            }
        },
    },
    yAxis3D: {
        type: 'category',
        name: null,
        nameTextStyle: {
            fontSize: 14,
        },
        nameGap: 30,
        nameLocation: 'end',
        axisLabel: {
            fontSize: 10,
        }
    },
    zAxis3D: {
        type: 'value',
        nameTextStyle: {
            fontSize: 14,
        },
        nameGap: 30,
        nameLocation: 'end',
        axisLabel: {
            fontSize: 10,
            margin: 12,
            formatter: function (value) {
                return parseFloat(value).toFixed(2);
            }
        }
    },
    grid3D: {
        boxWidth: 200,
        borderWidth: 0,
        boxDepth: 80,
        boxHeight: 80,
        viewControl: {
            projection: 'perspective',
            autoRotate: false,
            distance: 230,
            alpha: 30,
            beta: 15
        },
        light: {
            main: {
                intensity: 1.2,
                shadow: false
            },
            ambient: {
                intensity: 0.3
            }
        }
    },
    legend: {
        show: false
    },
    tooltip: {
        show: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 1,
        borderColor: '#ccc',
        textStyle: {
            color: '#333'
        }
    },
    toolbox: {
        show: false,
    }
};

const downloadChart = (chartInstance, filename = 'Echart', selectedAsset) => {
    if (chartInstance) {
        const chart = chartInstance;
        if (chart && typeof chart.getDataURL === 'function') {
            // Capture the chart exactly as rendered on screen. We intentionally do NOT
            // call setOption here: the getOption/setOption round-trip forces ECharts to
            // re-run the grid3D layout, which re-fits the 3D scene and shifts the z-axis
            // title onto the value labels in the export. Capturing the current state
            // keeps the exported chart pixel-identical to the on-screen chart.
            const pixelRatio = 2;
            const url = chart.getDataURL({
                pixelRatio,
                backgroundColor: '#fff'
            });

            const triggerDownload = (finalUrl) => {
                const link = document.createElement('a');
                link.download = `${filename}.png`;
                link.href = finalUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };

            const assetName = selectedAsset?.name;
            if (!assetName) {
                triggerDownload(url);
                return;
            }

            // Draw the asset name onto the captured image via a 2D canvas so the chart
            // itself is never re-laid-out. Positions/size are scaled by pixelRatio.
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    ctx.fillStyle = 'rgba(31, 31, 31, 1)';
                    ctx.font = `bold ${20 * pixelRatio}px sans-serif`;
                    ctx.textBaseline = 'top';
                    ctx.fillText(assetName, 50 * pixelRatio, 30 * pixelRatio);
                    triggerDownload(canvas.toDataURL('image/png'));
                } catch (err) {
                    // Fall back to the plain chart image if compositing fails.
                    triggerDownload(url);
                }
            };
            img.onerror = () => triggerDownload(url);
            img.src = url;
        }
    }
};

const chart2DConfig = {
    backgroundColor: 'white',
    grid: {
        left: '0.1%',
        right: '0.5%',
        bottom: '0%',
        top: '0%',
        height: 0,
        containLabel: true
    },
    xAxis: {
        type: 'value',
        min: 'dataMin',
        max: 'dataMax',
        show: false,
        splitLine: {
            show: false
        }
    },
    yAxis: {
        type: 'value',
        show: false,
        axisLabel: {
            show: false
        }
    },
    dataZoom: {
        type: 'slider',
        xAxisIndex: [0],
        height: 40,
        filterMode: 'none',
        top: 0,
        showDetail: true,
    },
};

const getFormattedData = (
    fftDataArray = [],
    selectedEchartView,
    selectedEchartLegend,
    selectedEchartBandSettings,
    sensorType,
) => {

    // Helper function to check if frequency should be included based on threshold
    const shouldIncludeFrequency = (frequency) => {
        return !selectedEchartBandSettings?.moreGraph?.filterThreshold ||
            frequency >= selectedEchartBandSettings.moreGraph.filterThreshold;
    };

    // Helper function to clamp magnitude value to maxAmplitude if it exceeds the limit
    const clampMagnitude = (magnitude) => {
        if (selectedEchartView === echartViews.FREQUENCY || !selectedEchartBandSettings?.moreGraph?.maxAmplitude) {
            return magnitude;
        }
        return Math.min(magnitude, selectedEchartBandSettings.moreGraph.maxAmplitude);
    };

    // Collect all data organized by sensor axis
    const seriesMap = {};
    const timestamps = [];
    const isMSRasset = sensorType === assetType.MACHSENSOR;

    const checkSelectedLegends = (sensorName) => {
        if (isMSRasset) {
            return sensorName.includes(selectedEchartLegend) &&
                sensorName.includes(selectedEchartBandSettings?.bearingTypes?.[0]);
        }
        return sensorName === selectedEchartLegend;
    };

    const checkBearingTypes = (bffItem) => {
        if (bffItem.name?.includes(bearingFrequencyTypes.BSF.name)) {
            return selectedEchartBandSettings?.bsfType &&
                selectedEchartBandSettings?.bearingTypes?.some(type => bffItem.bearingType.includes(type));
        } else if (bffItem.name?.includes(bearingFrequencyTypes.BPFI.name)) {
            return selectedEchartBandSettings?.bpfiType &&
                selectedEchartBandSettings?.bearingTypes?.some(type => bffItem.bearingType.includes(type));
        } else if (bffItem.name?.includes(bearingFrequencyTypes.BPFO.name)) {
            return selectedEchartBandSettings?.bpfoType &&
                selectedEchartBandSettings?.bearingTypes?.some(type => bffItem.bearingType.includes(type));
        }
    };

    fftDataArray.forEach((fft) => {
        const speed = fft.speed / 60;
        const timestampLabel = `${formatDateTime(normalizeUtcTimestamp(fft.timestamp))} — ${translate('ABB.Powertrain.Frontend.fftEchartSpeedXaxisText')}: ${fft?.speed !== null && fft?.speed !== undefined ? setMaxPrecision(Number(fft.speed), MAX_PRECISION) + ' ' + (fft?.speedUnitSymbol || SPEED_UNIT) : '—'}`;
        if (selectedEchartView === echartViews.FREQUENCY ||
            selectedEchartView === echartViews.ORDER_OF_SPEED &&
            selectedEchartBandSettings.bandsOnly &&
            speed >= 1
        ) {
            timestamps.push(timestampLabel);
            fft.dataValues?.forEach((point) => {
                if (!seriesMap[point.sensorAxisName]) {
                    seriesMap[point.sensorAxisName] = [];
                }
                if (checkSelectedLegends(point.sensorAxisName)) {
                    point?.sensorAxisDataValues?.forEach((value) => {
                        const frequency = selectedEchartView === echartViews.FREQUENCY ?
                            value.frequency : value.frequency / speed;
                        const originalMagnitude = value.magnitude;
                        const magnitude = clampMagnitude(originalMagnitude);

                        // Filter out data below threshold
                        if (shouldIncludeFrequency(frequency)) {
                            seriesMap[point.sensorAxisName].push({
                                frequency,
                                timestamp: timestampLabel,
                                magnitude,
                                originalMagnitude,
                                echartColor: selectedEchartView === echartViews.FREQUENCY ?
                                    echartColors.FREQUENCY : echartColors.SPEED
                            });
                        }
                    });
                }
            });
        }

        if (selectedEchartView === echartViews.ORDER_OF_SPEED && speed >= 1) {
            // Process bearing fault frequencies
            const getSensorName = (frequencyName) => {
                if (frequencyName?.includes(bearingFrequencyTypes.BSF.name)) {
                    return [bearingFrequencyTypes.BSF.name, echartColors.BSF];
                } else if (frequencyName?.includes(bearingFrequencyTypes.BPFI.name)) {
                    return [bearingFrequencyTypes.BPFI.name, echartColors.BPFI];
                } else if (frequencyName?.includes(bearingFrequencyTypes.BPFO.name)) {
                    return [bearingFrequencyTypes.BPFO.name, echartColors.BPFO];
                }
            };

            // Get spectrum data for the selected axis to interpolate magnitudes for special frequencies
            const spectrumData = getSpectrumDataForFrequency(fft, selectedEchartLegend);

            fft.bearingFaultFrequencies?.forEach((bffItem) => {
                const [sensorName, color] = getSensorName(bffItem.name);
                if (!seriesMap[sensorName]) {
                    seriesMap[sensorName] = [];
                }
                if (checkBearingTypes(bffItem)) {
                    const frequency = bffItem.value / speed;
                    const originalMagnitude = getSpecialFrequenciesMagnitudes(
                        bffItem.value,
                        spectrumData
                    );
                    const magnitude = clampMagnitude(originalMagnitude);

                    // Filter out data below threshold
                    if (shouldIncludeFrequency(frequency)) {
                        seriesMap[sensorName].push({
                            frequency,
                            timestamp: timestampLabel,
                            magnitude,
                            originalMagnitude,
                            echartColor: color
                        });
                    }
                }
            });

            // Process harmonics
            if (selectedEchartBandSettings.harmonics) {
                fft.harmonics?.forEach((harmonicItem) => {
                    const sensorName = translate(echartHarmonicsLabel);
                    if (!seriesMap[sensorName]) {
                        seriesMap[sensorName] = [];
                    }
                    const frequency = harmonicItem.value / speed;
                    const originalMagnitude = getSpecialFrequenciesMagnitudes(
                        harmonicItem.value,
                        spectrumData
                    );
                    const magnitude = clampMagnitude(originalMagnitude);

                    // Filter out data below threshold
                    if (shouldIncludeFrequency(frequency)) {
                        seriesMap[sensorName].push({
                            frequency,
                            timestamp: timestampLabel,
                            magnitude,
                            originalMagnitude,
                            echartColor: echartColors.HARMONICS
                        });
                    }
                });
            }
        }
    });

    // Convert to series data with proper format [frequencyIndex, timestamp, magnitude, originalMagnitude]
    const seriesData = [];
    Object.keys(seriesMap).forEach((sensorName) => {
        const chartData = seriesMap[sensorName].map(item => [
            item.frequency,
            item.timestamp,
            item.magnitude,
            item.originalMagnitude
        ]);
        if (chartData?.length) {
            const toleranceBandWidth = selectedEchartView === echartViews.FREQUENCY ? 0.1 : selectedEchartBandSettings?.moreGraph?.toleranceBandWidth || 0.1;
            seriesData.push({
                type: 'bar3D',
                id: sensorName,
                name: sensorName,
                data: chartData,
                shading: 'lambert',
                showInLegend: false,
                emphasis: {
                    label: {
                        show: false
                    }
                },
                itemStyle: {
                    color: seriesMap[sensorName][0]?.echartColor,
                },
                barSize: [toleranceBandWidth * 10, toleranceBandWidth * 10],
            });
        }
    });

    const series2DData = [];
    fftDataArray.forEach((fftData) => {
        const {
            dataValues: data,
        } = fftData || {};
        const speed = fftData.speed / 60;

        if (data &&
            (selectedEchartView === echartViews.FREQUENCY ||
                selectedEchartView === echartViews.ORDER_OF_SPEED && speed >= 1)
        ) {
            data?.forEach((item) => {
                if (checkSelectedLegends(item.sensorAxisName)) {
                    const seriesData = map(item.sensorAxisDataValues, (dataValues) => {
                        const frequency = selectedEchartView === echartViews.FREQUENCY ?
                            dataValues.frequency : dataValues.frequency / speed;
                        const originalMagnitude = dataValues.magnitude;
                        const magnitude = clampMagnitude(originalMagnitude);
                        return [frequency, magnitude, originalMagnitude];
                    }).filter(([frequency]) => {
                        // Filter out data below threshold
                        return shouldIncludeFrequency(frequency);
                    });

                    series2DData.push({
                        id: `${fftData?.timestamp}-${item.sensorAxisName}`,
                        type: 'line',
                        xAxisIndex: 0,
                        data: seriesData,
                        smooth: true,
                        showSymbol: false,
                        itemStyle: {
                            color: echartColors.FREQUENCY,
                        },
                    });
                }
            });
        }
    });

    return {
        seriesData,
        timestamps,
        series2DData
    };

};


const useChartConfig = (
    fftData,
    selectedEchartView,
    selectedEchartLegend,
    selectedEchartBandSettings,
    sensorType,
    selectedTimestamps,
    chartInstance,
    chart2DRef,
    frequencyRange,
    setFrequencyRange,
    selectedAsset,
) => {
    const [options, setOptions] = useState({});
    const [options2D, setOptions2D] = useState({});

    const on2DEvents = {
        datazoom: (e) => {
            if (chartInstance && chart2DRef?.current) {
                try {
                    const chart2DInstance = chart2DRef.current?.getEchartsInstance
                        ? chart2DRef.current.getEchartsInstance()
                        : chart2DRef.current;
                    const chart2DOption = chart2DInstance?.getOption();
                    if (chart2DOption) {
                        const dataZoom = chart2DOption.dataZoom[0];
                        const xAxis = chart2DOption.xAxis[0];

                        // The navigator spans the union of every series' x-range. In Order of
                        // Speed view with different speeds, each timestamp's series is divided by
                        // its own speed, so series ranges differ. Measuring only series[0] would
                        // give a too-narrow range and make the window shrink instead of expand,
                        // so gather x-values from all series to match the navigator's full range.
                        const allXValues = chart2DOption.series.flatMap((s) => (s.data || []).map((point) => point[0]));

                        // Calculate the actual min/max values based on dataZoom percentages
                        const dataMin = xAxis.min === 'dataMin' ?
                            getMinValue(allXValues) :
                            xAxis.min;
                        const dataMax = xAxis.max === 'dataMax' ?
                            getMaxValue(allXValues) :
                            xAxis.max;

                        const range = dataMax - dataMin;
                        const zoomedMin = dataMin + range * (dataZoom.start || e.start) / 100;
                        const zoomedMax = dataMin + range * (dataZoom.end || e.end) / 100;
                        setFrequencyRange({ min: zoomedMin, max: zoomedMax });
                    }
                } catch (err) {
                    // chart2DRef may point to a stale/unmounted instance (e.g. after popout closes)
                    console.log('Error resetting frequency range for Order of Speed view', err);
                }
            }
        },
    };

    // Helper function to filter data based on frequency range
    const filterDataByRange = (seriesData, range) => {
        if (range?.min === null || range?.max === null) {
            return seriesData;
        }
        return seriesData.map(series => ({
            ...series,
            data: series.data.filter(([frequency]) =>
                frequency >= range.min && frequency <= range.max
            )
        }));
    };

    useEffect(() => {
        setFrequencyRange({ min: null, max: null });
        if (selectedEchartView === echartViews.ORDER_OF_SPEED) {
            setTimeout(() => {
                try {
                    // For order of speed view, we want to reset frequency range when view changes to ensure all relevant data is shown
                    setFrequencyRange({ min: 0, max: 20 });
                    // Update 2D chart dataZoom max directly via ref
                    if (chart2DRef?.current) {
                        const chart2DInstance = chart2DRef.current?.getEchartsInstance
                            ? chart2DRef.current.getEchartsInstance()
                            : chart2DRef.current;
                        if (chart2DInstance && typeof chart2DInstance.setOption === 'function') {
                            chart2DInstance.setOption({
                                dataZoom: {
                                    startValue: 0,
                                    endValue: 20,
                                }
                            }, false);
                        }
                    }
                } catch (err) {
                    console.log('Error resetting frequency range for Order of Speed view', err);
                }
            }, 500); // Delay to ensure chart has updated with new view
        }
    }, [
        selectedEchartView,
        selectedTimestamps,
        selectedEchartLegend,
        fftData
    ]);

    const getXAxisName = () => {
        if (selectedEchartView === echartViews.FREQUENCY) {
            return fftData?.[0]?.frequencyUnitSymbol;
        }
        if (selectedEchartView === echartViews.ORDER_OF_SPEED) {
            return translate('ABB.Powertrain.Frontend.fftEchartOrderOfSpeedXaxisText');
        }
        return chartConfig.xAxis3D.name;
    };

    const createAxisLabelFormatter = (xAxisMin, xAxisMax) => {
        return (value) => {
            const formatted = Number.parseFloat(value).toFixed(2);
            if (xAxisMax !== null && formatted === Number.parseFloat(xAxisMax).toFixed(2)) {
                return '';
            }
            if (xAxisMin !== null && formatted === Number.parseFloat(xAxisMin).toFixed(2)) {
                return '  ' + formatted;
            }
            return formatted;
        };
    };

    const getNoDataGraphic = () => ({
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
            text: translate('ABB.Powertrain.Frontend.noDataLabelAvailable'),
            fill: '#999',
            fontSize: 20
        }
    });

    const get2DGraphic = (series2DData) => {
        if (series2DData.length === 0) {
            return [];
        }
        const flatFrequencies = series2DData.flatMap(s => s.data.map(p => p[0]));
        const maxFrequency = getMaxValue(flatFrequencies);
        const displayText = maxFrequency ? Number.parseFloat(maxFrequency).toFixed(2) : '';
        return [{
            type: 'text',
            right: 15,
            top: 'middle',
            z: 100,
            style: {
                text: displayText,
                fontSize: 11,
                fill: '#666',
            }
        }];
    };

    const renderTooltip = (params) => {
        if (selectedEchartView === echartViews.FREQUENCY) {
            return renderToStaticMarkup(
                <FrequencyView
                    points={params.data}
                    frequencyUnitSymbol={fftData?.[0]?.frequencyUnitSymbol}
                    magnitudeUnitSymbol={fftData?.[0]?.magnitudeUnitSymbol}
                    precision={fftData?.[0]?.precision}
                />
            );
        }
        return renderToStaticMarkup(
            <SpeedView
                points={params}
                frequencyUnitSymbol={fftData?.[0]?.frequencyUnitSymbol}
                magnitudeUnitSymbol={fftData?.[0]?.magnitudeUnitSymbol}
                precision={fftData?.[0]?.precision}
                speed={fftData?.[0]?.speed}
            />
        );
    };

    const update2DChartViaRef = (series2DData, dataZoom) => {
        if (!chart2DRef?.current) {
            return;
        }
        const chart2DInstance = chart2DRef.current?.getEchartsInstance
            ? chart2DRef.current.getEchartsInstance()
            : chart2DRef.current;
        if (chart2DInstance && typeof chart2DInstance.setOption === 'function') {
            chart2DInstance.setOption({
                series: series2DData,
                dataZoom
            }, false);
        }
    };

    // Build the 3D chart config from computed values
    const buildUpdatedChartConfig = ({
        seriesData,
        timestamps,
        allFrequencies,
        selectedEchartBandSettings,
        frequencyRange,
        fftData
    }) => {
        const dataMaxValue = getMaxValue(allFrequencies);
        const dataMinValue = getMinValue(allFrequencies);
        const xAxisMax = frequencyRange?.max === null ? dataMaxValue : frequencyRange.max;
        const xAxisMin = frequencyRange?.min === null ? dataMinValue : frequencyRange.min;
        const shouldUseDataMax = selectedEchartView === echartViews.FREQUENCY || selectedEchartBandSettings?.moreGraph?.maxAmplitude === null ||
            selectedEchartBandSettings?.moreGraph?.maxAmplitude === undefined;

        return {
            ...chartConfig,
            series: seriesData,
            ...seriesData.length === 0 ? { graphic: getNoDataGraphic() } : {},
            grid3D: { ...chartConfig.grid3D, show: seriesData.length > 0 },
            xAxis3D: {
                ...chartConfig.xAxis3D,
                show: seriesData.length > 0,
                name: getXAxisName(),
                axisLabel: {
                    ...chartConfig.xAxis3D.axisLabel,
                    formatter: createAxisLabelFormatter(xAxisMin, xAxisMax)
                },
                min: xAxisMin === null ? 'dataMin' : xAxisMin,
                max: xAxisMax === null ? 'dataMax' : xAxisMax,
            },
            yAxis3D: {
                ...chartConfig.yAxis3D,
                show: seriesData.length > 0,
                data: timestamps && timestamps.length > 0 ? timestamps : chartConfig.yAxis3D.data
            },
            zAxis3D: {
                ...chartConfig.zAxis3D,
                show: seriesData.length > 0,
                name: `${translate('ABB.Powertrain.Frontend.fftEchartZAxisText')} [${fftData?.[0]?.magnitudeUnitSymbol}]`,
                max: shouldUseDataMax ? 'dataMax' : selectedEchartBandSettings?.moreGraph?.maxAmplitude
            }
        };
    };

    // Build the 2D chart config from computed values
    const buildUpdatedChart2DConfig = ({ series2DData, frequencyRange, selectedEchartView }) => {
        const hasFrequencyRange = frequencyRange?.min !== null && frequencyRange?.max !== null;

        let dataZoomValues;
        if (hasFrequencyRange) {
            dataZoomValues = { startValue: frequencyRange.min, endValue: frequencyRange.max };
        } else if (selectedEchartView === echartViews.ORDER_OF_SPEED) {
            dataZoomValues = { startValue: 0, endValue: 20 };
        } else {
            dataZoomValues = { startValue: 'dataMin', endValue: 'dataMax' };
        }

        return {
            ...chart2DConfig,
            series: series2DData,
            graphic: get2DGraphic(series2DData),
            dataZoom: {
                ...chart2DConfig.dataZoom,
                ...dataZoomValues
            }
        };
    };

    useEffect(() => {
        const {
            seriesData,
            timestamps,
            series2DData
        } = getFormattedData(
            fftData,
            selectedEchartView,
            selectedEchartLegend,
            selectedEchartBandSettings,
            sensorType,
        );

        const filteredSeriesData = filterDataByRange(seriesData, frequencyRange);
        const allFrequencies = seriesData.flatMap(s => s.data.map(d => d[0]));

        const updatedChartConfig = buildUpdatedChartConfig({
            seriesData: filteredSeriesData,
            timestamps,
            allFrequencies,
            selectedEchartBandSettings,
            frequencyRange,
            fftData
        });

        chartConfig.tooltip.formatter = renderTooltip;
        setOptions(updatedChartConfig);

        if (seriesData.length === 0) {
            setOptions2D(null);
            return;
        }

        const updatedChart2DConfig = buildUpdatedChart2DConfig({
            series2DData,
            frequencyRange,
            selectedEchartView
        });

        const hasFrequencyRange = frequencyRange?.min !== null && frequencyRange?.max !== null;
        if (hasFrequencyRange && chart2DRef?.current) {
            update2DChartViaRef(series2DData, updatedChart2DConfig.dataZoom);
        } else {
            setOptions2D(updatedChart2DConfig);
        }
    }, [
        fftData,
        selectedTimestamps,
        selectedEchartView,
        selectedEchartLegend,
        selectedEchartBandSettings,
        frequencyRange
    ]);

    const downloadChartWithAsset = (chartInstance, filename = 'Echart') => {
        return downloadChart(chartInstance, filename, selectedAsset);
    };

    return [options, options2D, on2DEvents, downloadChartWithAsset];
};

export default useChartConfig;
